import React, { useMemo } from 'react';
import { Icon } from 'shared/components/Icon';
import './style.less';
import { OutboundCallReport, SourceType } from 'types/outbound';
import { useAppSelector } from 'shared/store';
import { selectActiveOrgMembers } from 'shared/store/auth-slice';
import { AvatarStack } from 'browser/components/AvatarStack';
import { format } from 'date-fns';
import { formatSecondsToTimer } from 'shared/utils/helpers';
import { StatusBadge } from 'browser/components/Outbound/CallTaskDisplay';
import {
  getActivityCallDisposition,
  getActivityContactList,
  getActivityIcon,
} from 'shared/utils/outbound';

export const ConnectedCallDetailActivityItem: React.FC<{
  activity: OutboundCallReport;
}> = ({ activity }) => {
  const {
    apolloContactLists,
    hubspotContactLists,
    outreachContactLists,
    salesforceContactLists,
    salesloftContactLists,
    servicebellContactLists,
    apolloCallDispositions,
    hubspotCallDispositions,
    outreachCallDispositions,
    salesforceCallDispositions,
    salesloftCallDispositions,
    servicebellCallDispositions,
  } = useAppSelector(state => state.outbound);
  const users = useAppSelector(selectActiveOrgMembers);
  const user = useMemo(() => {
    return users.find(user => user.id === activity.userId);
  }, [activity.userId, users]);

  const icon = useMemo(() => {
    return getActivityIcon(activity);
  }, [activity]);

  const callDisposition = useMemo(() => {
    return getActivityCallDisposition(
      activity,
      apolloCallDispositions,
      hubspotCallDispositions,
      outreachCallDispositions,
      salesforceCallDispositions,
      salesloftCallDispositions,
      servicebellCallDispositions,
    );
  }, [
    activity,
    apolloCallDispositions,
    hubspotCallDispositions,
    outreachCallDispositions,
    salesforceCallDispositions,
    salesloftCallDispositions,
    servicebellCallDispositions,
  ]);

  const contactListName = useMemo(() => {
    return getActivityContactList(
      activity,
      apolloContactLists,
      hubspotContactLists,
      outreachContactLists,
      salesforceContactLists,
      salesloftContactLists,
      servicebellContactLists,
    );
  }, [
    activity,
    apolloContactLists,
    hubspotContactLists,
    outreachContactLists,
    salesforceContactLists,
    salesloftContactLists,
    servicebellContactLists,
  ]);

  return (
    <div className="ConnectedCallDetailActivityItem">
      <div className="ConnectedCallDetailActivityItem__icon">
        <Icon icon="phoneRingingOutline" width={20} height={20} />
      </div>
      <div className="ConnectedCallDetailActivityItem__content">
        <div className="ConnectedCallDetailActivityItem__summary">
          <AvatarStack users={[user ?? []].flat()} overlap="tight" />
          <div className="ConnectedCallDetailActivityItem__message">
            <strong>{user?.displayName}</strong> called{' '}
            <strong>{activity.contact.name}</strong>
          </div>
          {activity.dateCreated && (
            <div className="ConnectedCallDetailActivityItem__date">
              {format(new Date(activity?.dateCreated), 'MM/dd/yyyy hh:mm a')}
            </div>
          )}
        </div>
        <div className="ConnectedCallDetailActivityItem__details">
          <div className="ConnectedCallDetailActivityItem__details-item">
            <div className="ConnectedCallDetailActivityItem__details-label">Duration</div>
            <div className="ConnectedCallDetailActivityItem__details-value--light">
              {activity.dialTask
                ? formatSecondsToTimer(activity.dialTask.callDuration ?? 0)
                : 'Unknown'}
            </div>
          </div>
          <div className="ConnectedCallDetailActivityItem__details-item">
            <div className="ConnectedCallDetailActivityItem__details-label">
              Dial List
            </div>
            <div className="ConnectedCallDetailActivityItem__details-value">
              {activity.dialTask?.sourceType !== SourceType.DIALPAD && (
                <>
                  <Icon width={16} height={16} icon={icon || 'outreach'} />
                  <span>{contactListName}</span>
                </>
              )}
              {activity.dialTask?.sourceType === SourceType.DIALPAD && 'Dialpad'}
            </div>
          </div>
          <div className="ConnectedCallDetailActivityItem__details-item">
            <div className="ConnectedCallDetailActivityItem__details-label">
              Disposition
            </div>
            {callDisposition && <StatusBadge field={callDisposition} />}
          </div>
          <div className="ConnectedCallDetailActivityItem__details-item--spacer"></div>
          <div className="ConnectedCallDetailActivityItem__details-item ConnectedCallDetailActivityItem__details-item--full">
            <div className="ConnectedCallDetailActivityItem__details-label">Notes</div>
            <div className="ConnectedCallDetailActivityItem__details-value--light">
              {activity?.notes}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
